/**
 * Индекс ударений проекта для просмотрщика (спек «Ударения в просмотрщике», «Обновление у всех сразу»).
 * Один на проект — записи общие для всех файлов. Правится точечно по project:glossaryChanged и ответам меню слова;
 * общий словарь и загрузка — перечёт с ETag не чаще раза в 500 мс (обычно 304), после переподключения — сверка.
 */
import type { QueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { StressEntry } from '@/api/glossaryModel';
import type { StressIndexEntry, StressIndexForm } from '@/api/stressIndexModel';
import { stressIndexes, yoIndexes } from '@stress/notation';
import { tokenizeWords } from '@stress/tokenize';
import type { PhraseStress, ViewerStressIndex, WordStress } from '../../../ui/document-viewer/stress/stressMarks';
import type { GlossaryChangedPayload } from '../stressCache';

export const stressIndexQueryKey = (projectId: string) => ['stressIndex', projectId] as const;
/**
 * etag — версия, с которой пришли данные; null — данные правились на месте и с версией сервера уже не совпадают
 * (иначе сверка получила бы 304 на версию, к которой сервер успел вернуться, и знак устаревшей правки остался бы)
 */
export type StressIndexData = { etag: string | null; entries: StressIndexEntry[] };

export function toIndexEntry(e: StressEntry): StressIndexEntry {
  return {
    entry_id: e.entry_id, scope: e.scope, kind: e.kind, word: e.word, word_key: e.word_key, stressed: e.stressed, pronunciation: e.pronunciation,
    updated_at: e.updated_at, stress_source: e.stress_source,
    forms: e.forms.map(({ form_id, form, form_key, variants, status, origin, added, stress_set }) => ({ form_id, form, form_key, variants, status, origin, added, stress_set })),
  };
}

const hasYo = (s: string) => /[ёЁ]/.test(s);

/** Ударение формы для знаков: утверждённая, со знаком или с «ё»; «?» (без ударения) и отклонённая — без знака */
function formStress(f: StressIndexForm): WordStress | null {
  if (f.status !== 'approved') return null;
  const v = f.variants[0];
  if (v !== undefined) return { stress: stressIndexes(v), yo: yoIndexes(v) };
  return hasYo(f.form) ? { stress: [], yo: yoIndexes(f.form) } : null;
}

type EntryValues = { own: [string, WordStress] | null; forms: Array<[string, WordStress]>; phrase: PhraseStress | null };
/** Значения неизменной записи — те же объекты: срез индекса по странице не меняется, страница не перерисовывается */
const VALUES = new WeakMap<StressIndexEntry, EntryValues>();

function valuesOf(e: StressIndexEntry): EntryValues {
  const cached = VALUES.get(e);
  if (cached) return cached;
  const v: EntryValues = { own: null, forms: [], phrase: null };
  if (e.kind === 'pronunciation') {
    if (e.pronunciation) v.phrase = { keys: tokenizeWords(e.word).map((t) => t.key), pronunciation: e.pronunciation };
  } else if (e.stressed) {
    v.own = [e.word_key, { stress: stressIndexes(e.stressed), yo: yoIndexes(e.stressed) }];
    for (const f of e.forms) {
      const s = formStress(f);
      if (s) v.forms.push([f.form_key, s]);
    }
  }
  VALUES.set(e, v);
  return v;
}

/** Глоссарий книги важнее общего словаря, запись важнее чужой формы: позже записанное в Map побеждает */
export function buildViewerIndex(entries: readonly StressIndexEntry[]): ViewerStressIndex {
  const words = new Map<string, WordStress>();
  const phraseByKey = new Map<string, PhraseStress>();
  const tiers = [entries.filter((e) => e.scope === 'shared'), entries.filter((e) => e.scope === 'book')];
  for (const tier of tiers) {
    for (const e of tier) for (const [k, s] of valuesOf(e).forms) words.set(k, s);
    for (const e of tier) {
      const v = valuesOf(e);
      if (v.own) words.set(v.own[0], v.own[1]);
      if (v.phrase && v.phrase.keys.length > 0) phraseByKey.set(e.word_key, v.phrase);
    }
  }
  const phrases = new Map<string, PhraseStress[]>();
  for (const p of phraseByKey.values()) phrases.set(p.keys[0], [...(phrases.get(p.keys[0]) ?? []), p]);
  return { words, phrases };
}

/** Запись по ключу слова страницы: сама запись или запись, чья это форма (меню слова открывает запись) */
export function entryForKey(entries: readonly StressIndexEntry[], key: string): { entry: StressIndexEntry; form: StressIndexForm | null } | null {
  for (const scope of ['book', 'shared'] as const) {
    const own = entries.find((e) => e.scope === scope && e.word_key === key);
    if (own) return { entry: own, form: null };
  }
  for (const scope of ['book', 'shared'] as const) {
    for (const e of entries) {
      if (e.scope !== scope) continue;
      const form = e.forms.find((f) => f.form_key === key && f.status === 'approved');
      if (form) return { entry: e, form };
    }
  }
  return null;
}

function upsertIndexEntry(data: StressIndexData | undefined, entry: StressIndexEntry): StressIndexData | undefined {
  if (!data) return data;
  const at = data.entries.findIndex((e) => e.entry_id === entry.entry_id);
  if (at < 0) return { etag: null, entries: [...data.entries, entry] };
  if (data.entries[at].updated_at > entry.updated_at) return data;
  const entries = [...data.entries];
  entries[at] = entry;
  return { etag: null, entries };
}

function removeIndexEntry(data: StressIndexData | undefined, entryId: string): StressIndexData | undefined {
  if (!data || !data.entries.some((e) => e.entry_id === entryId)) return data;
  return { etag: null, entries: data.entries.filter((e) => e.entry_id !== entryId) };
}

/** Открытый индекс правится; закрытый не создаётся — загрузится при открытии документа */
export function putIndexEntry(qc: QueryClient, projectId: string, entry: StressIndexEntry): void {
  const key = stressIndexQueryKey(projectId);
  if (qc.getQueryData(key) === undefined) return;
  qc.setQueryData<StressIndexData>(key, (d) => upsertIndexEntry(d, entry));
}

export function dropIndexEntry(qc: QueryClient, projectId: string, entryId: string): void {
  const key = stressIndexQueryKey(projectId);
  if (qc.getQueryData(key) === undefined) return;
  qc.setQueryData<StressIndexData>(key, (d) => removeIndexEntry(d, entryId));
}

/**
 * Перед локальной правкой (меню слова): перечёт индекса в полёте отменяется — его ответ, снятый до записи, затёр бы
 * новый знак. Вернуть «был ли перечёт»: тогда после записи индекс надо сверить снова (scheduleStressIndexRefresh).
 */
export async function holdStressIndex(qc: QueryClient, projectId: string): Promise<boolean> {
  const key = stressIndexQueryKey(projectId);
  if (qc.isFetching({ queryKey: key }) === 0) return false;
  await qc.cancelQueries({ queryKey: key });
  return true;
}

/** Событие project:glossaryChanged несёт запись целиком — индекс правится по её id */
export function applyStressIndexChanged(qc: QueryClient, payload: GlossaryChangedPayload): void {
  if ('deleted' in payload) dropIndexEntry(qc, payload.projectId, payload.entry_id);
  else putIndexEntry(qc, payload.projectId, toIndexEntry(payload.entry));
}

/** Пачка событий за это время даёт один запрос */
const REFRESH_MS = 500;
const timers = new WeakMap<QueryClient, Map<string, ReturnType<typeof setTimeout>>>();

/**
 * Перечёт индекса с ETag (общий словарь, загрузка глоссария): не чаще раза в 500 мс на проект.
 * Без projectId — все открытые индексы: общий словарь действует во всех проектах.
 */
export function scheduleStressIndexRefresh(qc: QueryClient, projectId?: string): void {
  const ids = projectId
    ? [projectId]
    : qc.getQueryCache().findAll({ queryKey: ['stressIndex'] }).map((q) => String(q.queryKey[1]));
  let pending = timers.get(qc);
  if (!pending) {
    pending = new Map();
    timers.set(qc, pending);
  }
  for (const id of ids) {
    if (pending.has(id)) continue;
    const map = pending;
    map.set(
      id,
      setTimeout(() => {
        map.delete(id);
        void qc.invalidateQueries({ queryKey: stressIndexQueryKey(id) });
      }, REFRESH_MS)
    );
  }
}

/** Запрос индекса со своей версией: 304 — те же данные (та же ссылка: страницы не перерисовываются) */
export async function loadStressIndex(qc: QueryClient, projectId: string, fileId: string): Promise<StressIndexData> {
  const prev = qc.getQueryData<StressIndexData>(stressIndexQueryKey(projectId));
  const r = await api.glossary.stressIndex(projectId, fileId, prev?.etag ?? null);
  if (r.notModified) {
    if (!prev) throw new Error('stress-index: 304 без данных в кэше');
    return prev;
  }
  return { etag: r.etag, entries: r.entries };
}
